'use client'

import { buttonVariants } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@brew/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@brew/ui/tooltip'
import {
  HandoffChevronDownIcon,
  HandoffChevronUpIcon,
} from '@/components/mcp/handoff-icons'
import { HandoffMenuContent } from '@/components/mcp/handoff-menu-content'
import { McpClientLogo } from '@/components/settings/mcp/mcp-client-logo'

export type AgentHandoffButtonProps = {
  /** Ready-to-run prompt passed to the selected external harness. */
  prompt: string
  /** Trigger label for the `button` variant. */
  label?: string
  /** Compact menu-only mode for dense canvas and title toolbars. */
  variant?: 'button' | 'icon'
  /** Where the connector setup link points. */
  setupHref?: string
  /** Tools can hand off through HTTP/SDK and do not require the MCP footer. */
  showConnectorFooter?: boolean
  /** Radix modal lock; keep false inside React Flow canvases. */
  modal?: boolean
  menuSide?: 'top' | 'right' | 'bottom' | 'left'
  menuAlign?: 'start' | 'center' | 'end'
  tooltip?: string
  className?: string
}

const DEFAULT_LABEL = 'Use in your agent'
const DEFAULT_TOOLTIP =
  'Continue this in ChatGPT, Cursor, or Claude — or copy the prompt for any agent'

/**
 * The single visual + interaction primitive for every Brew → external-agent
 * handoff. Prompt construction stays with thin feature wrappers; the Claude
 * mark, labeled dropdown, copy state, and compact mode stay consistent here.
 */
export function AgentHandoffButton({
  prompt,
  label = DEFAULT_LABEL,
  variant = 'button',
  setupHref = '/settings/mcp',
  showConnectorFooter = true,
  modal = true,
  menuSide = 'bottom',
  menuAlign = 'end',
  tooltip = DEFAULT_TOOLTIP,
  className,
}: AgentHandoffButtonProps) {
  const ChevronIcon =
    menuSide === 'top' ? HandoffChevronUpIcon : HandoffChevronDownIcon

  const trigger =
    variant === 'icon' ? (
      <button
        type="button"
        aria-label={label}
        className={cn(
          'relative flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground',
          className
        )}
      >
        <McpClientLogo
          domain="claude.com"
          name="Claude"
          className="size-4 rounded"
        />
        <HandoffChevronDownIcon className="absolute right-0.5 bottom-0.5 size-2.5 rounded-full bg-background" />
      </button>
    ) : (
      <button
        type="button"
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'flex shrink-0 items-center',
          className
        )}
      >
        <McpClientLogo
          domain="claude.com"
          name="Claude"
          className="size-3.5 rounded"
        />
        <span className="truncate">{label}</span>
        <ChevronIcon className="size-3.5 text-muted-foreground" />
      </button>
    )

  return (
    <DropdownMenu modal={modal}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent
        side={menuSide}
        align={menuAlign}
        sideOffset={8}
        className="w-72"
      >
        <HandoffMenuContent
          prompt={prompt}
          setupHref={setupHref}
          showConnectorFooter={showConnectorFooter}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
